import React from 'react';
import ReactDOM from "react-dom";
import { observer } from 'mobx-react';
import loadable from '@loadable/component';
import InlinePromptStore from 'stores/InlinePromptStore';
import * as placement from 'placement';
import * as api from 'Api';
import * as utils from 'Utils';
import payloadStore from 'prompt/src/stores/PayloadStore';
import visitorStore from 'prompt/src/stores/Visitor/VisitorStore';


const InlinePrompt = loadable(() => import('containers/InlinePrompt'));

// Kept outside the store so it isn't observed by MobX.
const wixRendererBound = new WeakSet();
const wixMountBound = new WeakSet();


function ensurePromptRuntime() {
  if (window.OnVoardPrompt) return;
  window.OnVoardPrompt = {
    'rendered': false,
    'viewed_prompts': [],
    'engaged_prompts': [],
    'closed_prompts': [],
    'pageview_id': utils.uniqueId(),
    'tracked_recommender_impressions': [],
  };
}


@observer
class App extends React.Component {
  constructor(props) {
    super(props);
    this.stores = [];
  }

  componentDidMount() {
    // Dedupe by EL (Wix GCs detached nodes) and by campaign id (a SELECTOR
    // campaign must not also be mounted from an explicit element).
    this.processedEls = new WeakSet();
    this.mountedIds = new Set();
    this.retryCancels = new Set();
    this.start();
  }

  componentWillUnmount() {
    if (this.retryCancels) {
      this.retryCancels.forEach((cancel) => cancel());
      this.retryCancels.clear();
    }
    document.removeEventListener('ov_wix_mount_restored', this.onWixMountRestored);
  }

  start = async () => {
    ensurePromptRuntime();
    const platform = utils.getEcommercePlatform();

    // Listen before the initial scan so Wix restore events aren't missed.
    if (platform === 'wix') {
      document.addEventListener('ov_wix_mount_restored', this.onWixMountRestored);
    }

    await this.loadExplicitMounts();
    await this.loadSelectorMounts();
  };

  onWixMountRestored = (evt) => {
    const detail = (evt && evt.detail) || {};
    if (detail.className !== 'ov-app-prompt-inline') return;
    if (!detail.el) return;
    if (this.processedEls && this.processedEls.has(detail.el)) return;

    const promptId = detail.el.getAttribute('data-prompt-id');
    const store = this.stores.find((row) => row.id === promptId);
    if (store) {
      store.setEl(detail.el);
      this.embedWidget(store);
      return;
    }

    this.processExplicitEl(detail.el);
  };

  deserializeRuntimeContext = async (response) => {
    payloadStore.setIpCountry(response.ip_country || '');
    payloadStore.setContact(response.contact || {});
    // Load the visitor activity ONCE. Every mount's payload carries the same visitor
    // blob, but re-deserializing it resets visitorStore's in-session state — so a late
    // second embed mounting after the visitor already played a first embed would wipe
    // that play before it round-trips to the server. Guard so multiple campaigns each
    // keep their own tracked activity intact.
    if (this._visitorLoaded || !response.visitor) return;
    if (!this._visitorLoadPromise) {
      this._visitorLoadPromise = visitorStore.deserialize(response.visitor).then(() => {
        this._visitorLoaded = true;
      });
    }
    await this._visitorLoadPromise;
  };

  getAccountId = () => {
    const ovData = window.OnVoardData || {};
    if (ovData.account_id) return ovData.account_id;
    const el = document.querySelector(".ov-app-prompt-inline[data-account-id], .ov-app-prompt[data-account-id]");
    if (el) return el.getAttribute('data-account-id');
    return null;
  };

  getPayloadQs = () => {
    const qs = {};
    const contactId = utils.getContactId ? utils.getContactId() : null;
    const cookieId = utils.getCookieId ? utils.getCookieId() : null;
    const visitorId = utils.getPromptVisitorId ? utils.getPromptVisitorId() : null;
    if (contactId) qs['contact_id'] = contactId;
    if (cookieId) qs['cookie_id'] = cookieId;
    if (visitorId) qs['visitor_id'] = visitorId;
    return qs;
  };

  // Explicit mounts: a Shopify app-block or a pasted CUSTOM snippet, each carrying
  // data-prompt-id. Fetch that one campaign and render straight into the element.
  loadExplicitMounts = async () => {
    const nodeList = document.querySelectorAll(".ov-app-prompt-inline[data-prompt-id]");
    await Promise.all(Array.from(nodeList).map((el) => this.processExplicitEl(el)));
  };

  processExplicitEl = (el) => {
    // Dedupe by EL so a fresh container with the same id after SPA nav re-processes.
    if (this.processedEls && this.processedEls.has(el)) return Promise.resolve();
    const promptId = el.getAttribute('data-prompt-id');
    if (!promptId) return Promise.resolve();
    if (this.processedEls) this.processedEls.add(el);

    const store = new InlinePromptStore();

    return new Promise((resolve) => {
      const successCallback = async (response) => {
        const promptData = response.prompt;
        if (!promptData || !promptData.id) {
          resolve();
          return;
        }

        await this.deserializeRuntimeContext(response);
        promptData.show_powered_by = response.show_powered_by;
        await store.deserialize(promptData);
        store.setEl(el);

        // Eligibility gate: an inline embed honours the targeting filters (device, geo,
        // URL, referer, visitor, contact, platform/custom props). Not a match → never
        // mount. Fail-open on a filter error so a transient geo/API hiccup can't blank a
        // placed widget. Display-timing filters (frequency, triggers, ...) never apply.
        let eligible = true;
        try { eligible = await store.prompt.evaluateEligibilityFilters(); } catch (e) { eligible = true; }
        if (!eligible) { resolve(); return; }

        this.stores.push(store);
        this.mountedIds.add(store.id);
        this.embedWidget(store);
        resolve();
      };

      const errorCallback = (errors) => {
        utils.printErrors(errors);
        resolve();
      };

      api.getPromptPayload(successCallback, errorCallback, promptId, this.getPayloadQs());
    });
  };

  // SELECTOR mounts: campaigns with no element on the page. Fetch the account's
  // inline campaigns and create a mount next to each one's CSS selector. This is
  // the code-free, cross-platform path (BigCommerce / EasyStore / Wix / non-ecom).
  loadSelectorMounts = async () => {
    const accountId = this.getAccountId();
    if (!accountId) return;

    const successCallback = async (response) => {
      const prompts = response.prompts || [];
      await this.deserializeRuntimeContext(response);

      for (const promptData of prompts) {
        const mode = promptData.settings && promptData.settings.placement ? promptData.settings.placement.mode : "";
        if (mode !== "SELECTOR") continue;
        if (this.mountedIds.has(promptData.id)) continue;

        promptData.show_powered_by = response.show_powered_by;
        const store = new InlinePromptStore();
        await store.deserialize(promptData);

        // Eligibility gate (see loadExplicitMounts): skip mounting where the targeting
        // filters don't match; fail-open on error.
        let eligible = true;
        try { eligible = await store.prompt.evaluateEligibilityFilters(); } catch (e) { eligible = true; }
        if (!eligible) continue;

        this.stores.push(store);
        this.mountedIds.add(store.id);

        const successTasks = () => this.embedWidget(store);

        placement.setupPlacement(store);
        if (store.isSelectorPlacement && !store.el) {
          // Anchor not in the DOM yet (theme still hydrating) — retry.
          let cancelRetry;
          cancelRetry = placement.retryInsert(store, () => {
            this.retryCancels.delete(cancelRetry);
            successTasks();
          });
          this.retryCancels.add(cancelRetry);
          continue;
        }

        if (store.el) successTasks();
      }
    };

    const errorCallback = (errors) => { utils.printErrors(errors); };
    api.getAccountInlinePromptPayloads(successCallback, errorCallback, accountId, this.getPayloadQs());
  };

  embedWidget = (store) => {
    if (!store.el) return;

    store.el.setAttribute('data-app-id', store.id);
    this.registerWixMount(store);

    // On Wix the captured node may be detached after a re-render; re-resolve it.
    if (utils.getEcommercePlatform() === 'wix' && store.el) {
      const mountKey = store.el.getAttribute('data-ov-mount-key');
      if (mountKey) {
        const live = document.querySelector(`[data-ov-mount-key="${mountKey}"]`);
        if (live && live !== store.el) store.setEl(live);
      }
    }

    ReactDOM.render(<div className="ov-wrapper"><InlinePrompt store={store} /></div>, store.el);
    store.setRendered(true);
    this.registerWixMountRenderer(store);
  };

  registerWixMount = (store) => {
    if (utils.getEcommercePlatform() !== 'wix') return;
    if (!store.el || wixMountBound.has(store)) return;
    wixMountBound.add(store);

    import('common/wix-utils').then((wixUtils) => {
      const mountKey = wixUtils.wixMountKey('ov-app-prompt-inline', store.id);
      const el = wixUtils.registerWixMount({
        key: mountKey,
        className: 'ov-app-prompt-inline',
        appName: 'prompt-inline',
        appId: store.id,
        extraAttrs: {
          'data-prompt-id': store.id,
          'data-account-id': store.prompt.accountId,
        },
        findExisting: () => document.querySelector(`.ov-app-prompt-inline[data-prompt-id="${store.id}"]`),
        resolvePlacement: () => placement.resolvePlacement(store),
      });

      if (el && el !== store.el) {
        store.setEl(el);
        this.embedWidget(store);
      }
    });
  };

  // Wix re-inserts the container after SPA navigation and calls this renderer so
  // React re-attaches to the fresh node. No-op on non-Wix platforms.
  registerWixMountRenderer = (store) => {
    if (utils.getEcommercePlatform() !== 'wix') return;
    if (!store.el) return;

    const mountKey = store.el.getAttribute('data-ov-mount-key');
    if (!mountKey) return;
    if (wixRendererBound.has(store)) return;
    wixRendererBound.add(store);

    import('common/wix-utils').then((wixUtils) => {
      wixUtils.setWixMountRenderer(mountKey, (newEl) => {
        store.setEl(newEl);
        store.setRendered(false);
        ReactDOM.render(<div className="ov-wrapper"><InlinePrompt store={store} /></div>, store.el);
        store.setRendered(true);
      });
    });
  };

  render() {
    // Inject the icon sprite the reused content components reference.
    return (
      <div>
        <div dangerouslySetInnerHTML={{__html: require('prompt/src/static/icons/bp-icon.svg').default}} />
        <div dangerouslySetInnerHTML={{__html: require('prompt/src/static/icons/fa-icon.svg').default}} />
      </div>
    )
  }
}


export default App;
